import React, { FC } from 'react';
import { Badge, Tooltip, usePermission } from 'portal-commons';
import {
  faCircleCheck,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CampaignDetail, UseCase } from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { remapTCRStatus } from '../../utils';

import DetailField from './DetailField';
import Section from './Section';
import SectionGrid from './SectionGrid';
import { getBadgeTypeByStatus } from '@/applications/CspPortal/Brands/utils';
import { CustomSwitch } from '@/shared_elements';

interface SummarySectionProps {
  campaign: CampaignDetail;
  useCases?: UseCase[];
  handleAttributeChange: (
    value: boolean,
    key: string,
    label: string
  ) => Promise<void>;
  expiredCampaign: boolean;
}

const SummarySection: FC<SummarySectionProps> = ({
  campaign,
  useCases = [],
  handleAttributeChange,
  expiredCampaign,
}) => {
  const { hasPermission } = usePermission();
  return (
    <Section title="Summary">
      <SectionGrid>
        <DetailField label="Brand DBA Name">
          {campaign?.brandName || campaign?.brand?.displayName}
        </DetailField>

        <DetailField label="Use Case">
          {useCases.find((uc) => uc.id === campaign?.usecase)?.displayName ||
            campaign?.usecase ||
            'N/A'}
        </DetailField>

        <DetailField label="TCR Status">
          <Badge
            size="lg"
            type={getBadgeTypeByStatus(campaign?.status)}
            startAdornment={
              campaign?.status === 'ACTIVE' ? faCircleCheck : undefined
            }
          >
            {campaign?.status ? humanize(remapTCRStatus(campaign?.status)) : ''}
          </Badge>
        </DetailField>

        <DetailField label="DCA Elections Complete">
          {campaign?.dcaCompletion ? 'Yes' : 'No'}
        </DetailField>

        <DetailField label="Reference ID">{campaign?.referenceId}</DetailField>

        <DetailField label="Registered On">
          {convertTimeWithTimezone(
            campaign?.createDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </DetailField>

        <DetailField
          label={campaign?.autoRenewal ? 'Renewal Date' : 'Expiration Date'}
        >
          {convertTimeWithTimezone(
            campaign?.nextRenewalOrExpirationDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </DetailField>

        <DetailField
          label="Auto Renewal"
          labelIcon={
            <Tooltip
              title={`If set to "No" , the program will be deactivated at the end of the active period.`}
              style={{ position: 'static' }}
            >
              <FontAwesomeIcon icon={faInfoCircle} fontSize="16" />
            </Tooltip>
          }
        >
          <CustomSwitch
            val={campaign?.autoRenewal}
            disabled={
              !hasPermission('rcsCampaignDetail.autoRenewalToggle') ||
              expiredCampaign
            }
            handleChange={() => {
              if (
                campaign?.status !== 'EXPIRED' &&
                hasPermission('campaignDetail.autoRenewalToggle')
              ) {
                handleAttributeChange(
                  !campaign?.autoRenewal,
                  'autoRenewal',
                  'Auto renewal'
                );
              }
            }}
          />
        </DetailField>
      </SectionGrid>
    </Section>
  );
};

export default SummarySection;
